import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { BedDouble, LogIn, LogOut, Percent, Sparkles, Wallet } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Panel, StatusBadge, PayText, SOURCE_LABEL } from "@/components/extranet/StatusBadge";
import { http, idr, fmtDate } from "@/lib/api";
import { useT } from "@/lib/i18n";
import SyncStatusCard from "@/components/extranet/SyncStatusCard";

const Stat = ({ icon: I, label, value, sub, testid }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid={testid}>
    <div className="flex items-center justify-between text-slate-500"><span className="text-sm font-medium">{label}</span><I className="h-4 w-4" /></div>
    <p className="mt-2 font-ex-heading text-3xl font-extrabold tracking-tight">{value}</p>
    {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
  </div>
);

const GuestList = ({ items, empty, testid }) => (
  <ul className="divide-y divide-slate-100" data-testid={testid}>
    {items.length === 0 && <li className="px-5 py-6 text-sm text-slate-400">{empty}</li>}
    {items.map((b) => (
      <li key={b.code} className="flex items-center justify-between px-5 py-3 text-sm">
        <div><p className="font-semibold">{b.guest.name}</p><p className="text-xs text-slate-500">{b.room_name} · {b.nights} malam</p></div>
        <StatusBadge status={b.status} />
      </li>
    ))}
  </ul>
);

export default function Dashboard() {
  const { property } = useOutletContext();
  const t = useT();
  const [d, setD] = useState(null);
  useEffect(() => { http.get(`/extranet/properties/${property.id}/dashboard`).then((r) => setD(r.data)); }, [property.id]);
  if (!d) return <div className="grid gap-5 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28" />)}</div>;
  return (
    <div className="flex flex-col gap-5 fade-up">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">{t("Selamat datang, tim")} {property.name}</h1>
        <p className="text-sm text-slate-500">{t("Ringkasan untuk")} {fmtDate(d.today, "EEEE, d MMMM yyyy")}</p>
      </div>
      <SyncStatusCard propertyId={property.id} t={t} />
      <div className="grid grid-cols-2 gap-5 lg:grid-cols-6">
        <Stat testid="stat-arrivals" icon={LogIn} label={t("Kedatangan")} value={d.arrivals.length} sub={t("hari ini")} />
        <Stat testid="stat-departures" icon={LogOut} label={t("Keberangkatan")} value={d.departures.length} sub={t("hari ini")} />
        <Stat testid="stat-inhouse" icon={BedDouble} label={t("Sedang menginap")} value={d.in_house} sub={t("reservasi")} />
        <Stat testid="stat-occupancy" icon={Percent} label={t("Okupansi")} value={`${d.occupancy_pct}%`} sub={`${d.rooms_sold_today}/${d.rooms_total} kamar`} />
        <Stat testid="stat-new" icon={Sparkles} label={t("Reservasi baru")} value={d.new_reservations_7d} sub={`7 hari · ${d.pending} menunggu`} />
        <Stat testid="stat-revenue" icon={Wallet} label={t("Pendapatan bulan ini")} value={<span className="text-xl">{idr(d.revenue_month)}</span>} sub={t("reservasi terkonfirmasi")} />
      </div>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-4">
        <Panel title={t("Tren pendapatan 14 hari")} className="lg:col-span-2" testid="revenue-chart">
          <div className="h-64 p-4">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={d.revenue_trend}>
                <defs><linearGradient id="rv" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#1F6A96" stopOpacity={0.35} /><stop offset="100%" stopColor="#3E7C59" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" tickFormatter={(v) => fmtDate(v, "d/M")} fontSize={11} />
                <YAxis tickFormatter={(v) => `${Math.round(v / 1e6)}jt`} fontSize={11} width={40} />
                <Tooltip formatter={(v) => idr(v)} labelFormatter={(v) => fmtDate(v)} />
                <Area dataKey="revenue" stroke="#1F6A96" strokeWidth={2} fill="url(#rv)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title={t("Kedatangan hari ini")}><GuestList testid="arrivals-list" items={d.arrivals} empty="Tidak ada kedatangan" /></Panel>
        <Panel title={t("Keberangkatan hari ini")}><GuestList testid="departures-list" items={d.departures} empty="Tidak ada keberangkatan" /></Panel>
      </div>
      <Panel title={t("Reservasi terbaru")} testid="recent-reservations"
        action={<Link to="/extranet/reservations" data-testid="view-all-reservations" className="text-sm font-semibold text-[#006CE4] hover:underline">{t("Lihat semua")}</Link>}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC] text-left text-xs uppercase text-slate-500">
              <tr><th className="px-5 py-2.5">Kode</th><th>Tamu</th><th>Kamar</th><th>Menginap</th><th>Sumber</th><th>Status</th><th className="pr-5 text-right">Total</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {d.recent.map((b) => (
                <tr key={b.code}>
                  <td className="px-5 py-3 font-mono-x text-xs">{b.code}</td>
                  <td className="font-medium">{b.guest.name}</td><td>{b.room_name}</td>
                  <td className="text-slate-600">{fmtDate(b.check_in, "d MMM")} – {fmtDate(b.check_out, "d MMM")}</td>
                  <td className="capitalize text-slate-600">{SOURCE_LABEL(b.source)}</td>
                  <td><StatusBadge status={b.status} /> <PayText status={b.payment_status} /></td>
                  <td className="pr-5 text-right font-semibold">{idr(b.grand_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
